"use client";

import Link from "next/link";
import Image from "next/image";
import { trackCouponClick } from "@/app/actions";
import { getDiscountDisplay } from "@/lib/offer-display";
import { storePagePath } from "@/lib/page-type";

interface CouponGridCardProps {
    id: string;
    title: string;
    /** Not rendered by the card (kept so existing call sites compile). */
    code?: string;
    affiliateLink?: string;
    storeSlug: string;
    storeName: string;
    storeId: string;
    description?: string;
    storeLogo?: string;
    type?: string;
    discountValue?: number | null;
    discountType?: "PERCENT" | "FIXED" | "FREE_SHIPPING" | "DEAL" | null;
    verified?: boolean;
    successRate?: number | null;
    /** Kept so existing call sites compile; the V5 card has a single action size. */
    isLargeButton?: boolean;
    /** Visible keyword after the store name: "coupons" or "promo codes" (lib/store-seo `storeAnchorSuffix`). */
    anchorSuffix?: string;
    /** Page the store's offers open on (default `/{storeSlug}/coupons`); a REFERRAL store passes `/{slug}/referral-code`. */
    pagePath?: string;
    /** Store.pageType: a REFERRAL store's offers open on /{slug}/referral-code (ignored when `pagePath` is given). */
    pageType?: string | null;
}

/**
 * Compact offer card used on the home page, the category pages, Top Coupons and the store page's similar offers.
 * Layout (see `.cs-offer` in design-shell.css): merchant identity (logo, name, status) / dotted divider /
 * discount and action. The click flow is unchanged: tracking, then the existing `/out/{id}` redirect.
 * The store name links to the store page with the visible text "{Name} {coupons | promo codes}". The logo link next to
 * it is the same destination: hidden from assistive tech and the tab order (the text link is the one to use), and its
 * image alt carries the same words, so both read the same to a crawler.
 */
export default function CouponGridCard({
    id,
    title,
    description,
    storeSlug,
    storeName,
    storeId,
    storeLogo,
    type = "CODE",
    discountValue,
    discountType,
    verified,
    anchorSuffix = "coupons",
    pagePath,
    pageType,
}: CouponGridCardProps) {
    const isDeal = type === "DEAL";
    const discountDisplay = getDiscountDisplay(title, discountType, discountValue, description);
    const storeHref = pagePath ?? storePagePath({ slug: storeSlug, pageType });

    const handleGetCode = async () => {
        // Track click for analytics
        trackCouponClick(id, storeId, 'reveal');

        if (storeId) {
            setTimeout(() => {
                window.location.href = `/out/${id}`;
            }, 100);
        }
    };

    return (
        <article className="cs-offer">
            <div className="cs-offer__head">
                <Link
                    href={storeHref}
                    aria-hidden="true"
                    tabIndex={-1}
                    className="cs-offer__logo"
                >
                    {storeLogo ? (
                        <Image
                            src={storeLogo}
                            alt={`${storeName.trim()} ${anchorSuffix}`}
                            width={32}
                            height={32}
                        />
                    ) : (
                        <span className="text-xs font-bold text-white">{storeName.slice(0, 2)}</span>
                    )}
                </Link>
                <Link href={storeHref} className="cs-offer__name" title={`View ${storeName} ${anchorSuffix}`}>
                    {`${storeName} `}<span className="cs-kw">{anchorSuffix}</span>
                </Link>
                {verified ? (
                    <div className="cs-offer__status cs-offer__status--verified">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Verified
                    </div>
                ) : (
                    <div className="cs-offer__status">Active</div>
                )}
            </div>

            <div className="cs-offer__body">
                <div className="cs-offer__value">
                    {discountDisplay ? (
                        <div className="cs-offer__amount">
                            {discountDisplay.isPercent ? (
                                <>
                                    <span className="cs-offer__amount-num">{discountDisplay.value.replace('%', '')}</span>
                                    <span className="cs-offer__amount-unit" aria-hidden="true">
                                        <span>%</span>
                                        <span>OFF</span>
                                    </span>
                                    <span className="sr-only">% off</span>
                                </>
                            ) : (
                                <>
                                    <span className="cs-offer__amount-num">{discountDisplay.value}</span>
                                    <span className="cs-offer__amount-off">OFF</span>
                                </>
                            )}
                        </div>
                    ) : (
                        <div>
                            <div className="cs-offer__deal-label">DEAL</div>
                            <div className="cs-offer__deal-text">{title}</div>
                        </div>
                    )}
                </div>

                <a
                    href={`${storeHref}#couponId-${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleGetCode}
                    className={`cs-cta ${isDeal ? "cs-cta--deal" : "cs-cta--code"}`}
                    data-kind={isDeal ? "deal" : "code"}
                >
                    {isDeal ? "Get Deal" : "Get Code"}
                </a>
            </div>
        </article>
    );
}
